<script setup lang="ts">
import { computed } from 'vue';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import Slider from 'primevue/slider';
import type { SavedActivityFilter } from '@apparatix/schemas/Activities';
import type { User } from '@apparatix/schemas/Owner';
import type { StandardSelection } from '@apparatix/schemas/Selection';
import { Noir } from '@apparatix/theme/Noir';
import { useActivities, useDateFns, useUtilities } from '@apparatix/composables';
import { Circle, InputGroup, Toolbar, Typography } from '@apparatix/library/core';
import UserRoleGate from '@apparatix/library/UserRoleGate.vue';
import RadioFor from '@apparatix/library/RadioFor.vue';

type Props = {
  layout: 'column' | 'row';
  mode: 'filter' | 'editor';
  typeSelections: StandardSelection[],
  methodSelections: StandardSelection[],
  purposeSelections: StandardSelection[],
  userSelections: User[],
  otherSectionSelections?: SavedActivityFilter[],
  saveDisabled?: boolean,
  resetDisabled?: boolean,
  searchDisabled?: boolean,
};

type Emits = {
  (e: 'save', filter: SavedActivityFilter): void,
  (e: 'search', filter: SavedActivityFilter): void,
  (e: 'reset'): void,
};

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const model = defineModel<SavedActivityFilter>();

const { parse } = useDateFns();
const { filterMagicNumbers, getLabelForVariableDayRange, getDisplayType } = useActivities();
const { arrayHasValues } = useUtilities();

const dateRange = computed({
  get() {
    if (!model.value?.minActivityDate && !model.value?.maxActivityDate) {
      return undefined;
    }

    return [
      model.value.minActivityDate ? parse(model.value.minActivityDate) : null,
      model.value.maxActivityDate ? parse(model.value.maxActivityDate) : null,
    ];
  },
  set(values) {
    if (!Array.isArray(values)) {
      model.value.minActivityDate = undefined;
      model.value.maxActivityDate = undefined;
      return;
    }

    const [min, max] = values;
    model.value.minActivityDate = min instanceof Date ? min.toISOString() : undefined;
    model.value.maxActivityDate = max instanceof Date ? max.toISOString() : undefined;
  },
});

const types = computed({
  get() {
    return props.typeSelections.filter(type => model.value?.type?.includes(type.id));
  },
  set(value) {
    model.value.type = value.map(type => type.id);
  },
});

const methods = computed({
  get() {
    return props.methodSelections.filter(method => model.value?.method?.includes(method.id));
  },
  set(value) {
    model.value.method = value.map(method => method.id);
  },
});

const purpose = computed({
  get() {
    return props.purposeSelections.filter(option => model.value?.purpose?.includes(option.id));
  },
  set(value) {
    model.value.purpose = value.map(p => p.id);
  },
});

const users = computed({
  get() {
    return props.userSelections.filter(user => model.value?.users?.includes(user.id));
  },
  set(values) {
    model.value.users = values.map(user => user.id);
  },
});

const excludeFromOtherSections = computed({
  get() {
    if (!arrayHasValues(props.otherSectionSelections)) {
      return [];
    }

    return props.otherSectionSelections.filter(section => model.value?.excludeFromOtherSections?.includes(section.key));
  },
  set(values) {
    model.value.excludeFromOtherSections = values.map(section => section.key);
  },
});

const clearDateRange = () => {
  model.value.minActivityDate = undefined;
  model.value.maxActivityDate = undefined;
};

const onFilterApply = () => emit('search', model.value);

const onFilterReset = () => emit('reset');

const onFilterSave = () => emit('save', model.value);
</script>
<template>
  <div class="flex" :class="{'flex-row items-center gap-2': props.layout === 'row', 'flex-col gap-4': props.layout === 'column'}">
    <InputGroup v-if="props.mode === 'editor'" fluid>
      <Typography variant="form-label">
        Filter name
      </Typography>
      <InputText v-model="model.filterName" fluid />
    </InputGroup>
    <InputGroup :fluid="props.layout === 'column'">
      <Typography v-if="props.mode === 'editor'" variant="form-label">
        Types
      </Typography>
      <MultiSelect
        v-model="types"
        :options="props.typeSelections"
        :option-label="val => getDisplayType(val.name)"
        placeholder="Select types"
        :fluid="props.layout === 'column'"
      />
    </InputGroup>
    <InputGroup :fluid="props.layout === 'column'">
      <Typography v-if="props.mode === 'editor'" variant="form-label">
        Methods
      </Typography>
      <MultiSelect
        v-model="methods"
        :options="props.methodSelections"
        option-label="displayName"
        placeholder="Select methods"
        :fluid="props.layout === 'column'"
      />
    </InputGroup>
    <InputGroup :fluid="props.layout === 'column'">
      <Typography v-if="props.mode === 'editor'" variant="form-label">
        Purposes
      </Typography>
      <MultiSelect
        v-model="purpose"
        :options="props.purposeSelections"
        option-label="displayName"
        placeholder="Select purposes"
        :fluid="props.layout === 'column'"
      />
    </InputGroup>
    <UserRoleGate :allow-for="['Sales Manager']">
      <InputGroup :fluid="props.layout === 'column'">
        <Typography v-if="props.mode === 'editor'" variant="form-label">
          Activity owners
        </Typography>
        <MultiSelect
          v-model="users"
          :options="props.userSelections"
          :max-selected-labels="3"
          option-label="fullName"
          placeholder="Select users"
          filter
          :fluid="props.layout === 'column'"
        />
      </InputGroup>
    </UserRoleGate>
    <InputGroup v-if="props.mode === 'editor'" fluid>
      <Typography variant="form-label">
        Show Activities starting
      </Typography>
      <Slider
        v-model="model.dayRange"
        :min="filterMagicNumbers.noMinimum"
        :max="filterMagicNumbers.noMaximum"
        range
      />
      <Toolbar align="left">
        <Typography variant="small-body" muted>
          {{ getLabelForVariableDayRange(model.dayRange[0], 'min') }}
        </Typography>
        <Typography variant="small-body" muted>
          &mdash;
        </Typography>
        <Typography variant="small-body" muted>
          {{ getLabelForVariableDayRange(model.dayRange[1], 'max') }}
        </Typography>
      </Toolbar>
    </InputGroup>
    <InputGroup :fluid="props.layout === 'column'">
      <Typography v-if="props.mode === 'editor'" variant="form-label">
        Specific date range
      </Typography>
      <DatePicker
        v-model="dateRange"
        selection-mode="range"
        :manual-input="false"
        hide-on-range-selection
        show-button-bar
        placeholder="Date range"
        :fluid="props.layout === 'column'"
        @clear-click="clearDateRange"
      />
    </InputGroup>
    <Toolbar align="left">
      <RadioFor label="Include completed">
        <template #default="{id}">
          <Checkbox v-model="model.includeCompleted" :input-id="id" binary />
        </template>
      </RadioFor>
    </Toolbar>
    <Toolbar v-if="props.mode === 'editor'" align="left">
      <RadioFor label="Include incomplete">
        <template #default="{id}">
          <Checkbox v-model="model.includeIncomplete" :input-id="id" binary />
        </template>
      </RadioFor>
    </Toolbar>
    <template v-if="props.mode === 'editor'">
      <InputGroup v-if="arrayHasValues(props.otherSectionSelections)" fluid>
        <Typography variant="form-label">
          When an Activity is visible in this filter, exclude it from other filters
        </Typography>
        <MultiSelect
          v-model="excludeFromOtherSections"
          :options="props.otherSectionSelections"
          option-label="filterName"
          placeholder="Select sections"
        />
      </InputGroup>
      <Toolbar align="left">
        <RadioFor label="Load Activities on page load">
          <template #default="{id}">
            <Checkbox v-model="model.immediateLoad" :input-id="id" binary />
          </template>
        </RadioFor>
      </Toolbar>
      <Toolbar align="left">
        <Typography variant="form-label">
          Badge color
        </Typography>
        <Circle
          background="var(--p-red-500)"
          color="var(--white)"
          border-color="var(--p-red-500)"
          class="cursor-pointer p-2.5"
          :width="25"
          :height="25"
          @click="model.badgeSeverity = 'danger'"
        >
          <i v-if="model.badgeSeverity === 'danger'" class="pi pi-check" />
        </Circle>
        <Circle
          background="var(--p-slate-950)"
          border-color="var(--p-slate-950)"
          color="var(--white)"
          class="cursor-pointer p-2.5"
          :width="25"
          :height="25"
          @click="model.badgeSeverity = 'primary'"
        >
          <i v-if="model.badgeSeverity === 'primary'" class="pi pi-check" />
        </Circle>
        <Circle
          background="var(--p-green-500)"
          border-color="var(--p-green-500)"
          color="var(--white)"
          class="cursor-pointer p-2.5"
          :width="25"
          :height="25"
          @click="model.badgeSeverity = 'success'"
        >
          <i v-if="model.badgeSeverity === 'success'" class="pi pi-check" />
        </Circle>
      </Toolbar>
    </template>
    <Toolbar v-if="props.mode === 'filter'" align="left">
      <Button
        icon="pi pi-search"
        variant="outlined"
        aria-label="Apply filter"
        :disabled="props.searchDisabled"
        rounded
        @click="onFilterApply"
      />
      <Button
        icon="pi pi-undo"
        variant="outlined"
        severity="secondary"
        aria-label="Reset filter"
        :dt="Noir.button"
        :disabled="props.resetDisabled"
        rounded
        @click="onFilterReset"
      />
      <Button
        icon="pi pi-save"
        variant="outlined"
        severity="secondary"
        aria-label="Save filter"
        :dt="Noir.button"
        :disabled="props.saveDisabled"
        rounded
        @click="onFilterSave"
      />
    </Toolbar>
  </div>
</template>
